/**
 * LayeredAutocompleteSelect: reusable single-select Autocomplete dropdown.
 * Matches queries anywhere in the label, value, or extra search text by default.
 */
import React, { useMemo } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import type { SxProps, Theme } from "@mui/material/styles";
import { OVERLAY_SAFE_POPUP_Z_INDEX } from "../utils/overlayMenu";
import { disambiguateOptionLabels } from "../utils/selectOptionLabels";

export interface LayeredSelectOption {
  label: string;
  value: string;
  /** Extra type-to-filter text (description, uid). Label is still what the field shows. */
  searchText?: string;
  disabled?: boolean;
}

const stringifySelectOption = (option: LayeredSelectOption) =>
  `${option.searchText || ""} ${option.label} ${option.value}`;

const MATCH_ANY_FILTER = createFilterOptions<LayeredSelectOption>({
  matchFrom: "any",
  stringify: stringifySelectOption,
});

const MATCH_START_FILTER = createFilterOptions<LayeredSelectOption>({
  matchFrom: "start",
  stringify: stringifySelectOption,
});

interface LayeredAutocompleteSelectProps {
  id?: string;
  label: string;
  value: string;
  options: LayeredSelectOption[];
  onChange: (value: string) => void;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  /** Marks the field invalid and colors helper text as an error. */
  error?: boolean;
  noOptionsText?: string;
  disableClearable?: boolean;
  fullWidth?: boolean;
  size?: TextFieldProps["size"];
  /** Shown in the empty Autocomplete input. */
  placeholder?: string;
  /** Accessible name when the visible TextField label is hidden by CSS. */
  inputAriaLabel?: string;
  /**
   * Substring matching for type-to-filter. `any` matches anywhere in the
   * option label or value (default); `start` matches a prefix.
   */
  matchFrom?: "start" | "any";
  /** Passed through to the underlying TextField (MUI helper text under the control). */
  helperText?: React.ReactNode;
  sx?: SxProps<Theme>;
  textFieldSx?: SxProps<Theme>;
  disablePortal?: boolean;
  listboxMaxHeight?: number;
  /**
   * When > 0, sets listbox/paper minHeight. Default 0 so filtered Autocomplete options can shrink
   * (HITL status search); use a positive value only when a minimum menu height is required.
   */
  listboxMinHeight?: number;
}

const LayeredAutocompleteSelect: React.FC<LayeredAutocompleteSelectProps> = ({
  id,
  label,
  value,
  options,
  onChange,
  onClick,
  disabled = false,
  loading = false,
  required = false,
  error = false,
  noOptionsText = "No options",
  disableClearable = false,
  fullWidth = true,
  size,
  placeholder,
  inputAriaLabel,
  matchFrom = "any",
  helperText,
  sx,
  textFieldSx,
  disablePortal = false,
  listboxMaxHeight = 320,
  listboxMinHeight = 0,
}) => {
  const displayOptions = useMemo(() => disambiguateOptionLabels(options), [options]);
  const selectedOption = displayOptions.find((option) => option.value === value) ?? null;
  const listboxMinSx = listboxMinHeight > 0 ? { minHeight: listboxMinHeight } : {};
  const filterOptions = matchFrom === "any" ? MATCH_ANY_FILTER : matchFrom === "start" ? MATCH_START_FILTER : undefined;

  return (
    <Autocomplete<LayeredSelectOption, false, boolean, false>
      id={id}
      options={displayOptions}
      value={selectedOption}
      onChange={(_, next) => onChange(next?.value ?? "")}
      onClick={onClick}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.value}
      getOptionDisabled={(option) => !!option.disabled}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      filterOptions={filterOptions}
      disablePortal={disablePortal}
      disableClearable={disableClearable}
      loading={loading}
      noOptionsText={noOptionsText}
      fullWidth={fullWidth}
      disabled={disabled}
      openOnFocus
      slotProps={{
        popper: {
          sx: {
            zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
            pointerEvents: "auto",
          },
        },
        paper: {
          sx: {
            maxHeight: listboxMaxHeight,
            ...listboxMinSx,
            overflow: "hidden",
          },
        },
      }}
      ListboxProps={{
        style: {
          maxHeight: listboxMaxHeight,
          ...listboxMinSx,
          overflowY: "auto",
        },
      }}
      sx={{
        "& .MuiAutocomplete-paper": {
          maxHeight: listboxMaxHeight,
          ...listboxMinSx,
        },
        "& .MuiAutocomplete-listbox": {
          maxHeight: listboxMaxHeight,
          ...listboxMinSx,
          overflowY: "auto",
        },
        ...((sx || {}) as object),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          hiddenLabel={!label}
          label={label || undefined}
          placeholder={placeholder}
          helperText={helperText}
          required={required}
          error={error}
          sx={{
            "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
            "& .MuiInputBase-root": { fontFamily: "var(--theo-font-sans)" },
            ...((textFieldSx || {}) as object),
          }}
          inputProps={{
            ...params.inputProps,
            ...(inputAriaLabel ? { "aria-label": inputAriaLabel } : {}),
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default LayeredAutocompleteSelect;
